import { ChevronRight, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type AuctionRow = {
  id: string;
  referenceNumber: string;
  title: string;
  status: string;
  transportOrdered: boolean;
  currentBid: number;
  bidCount: number;
  startingBid: number;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  orgId: string;
  orgName: string;
  orgSlug: string;
  make: string | null;
  model: string | null;
  modelYear: number | null;
  type: "regular" | "flash";
};

type Tab = "all" | "live" | "pending_review" | "changes_requested" | "draft" | "closed";

const CLOSED_STATUSES = ["sold", "no_sale", "contract_pending", "invoice_pending", "payment_pending", "ready_for_pickup", "picked_up", "in_transit", "delivered", "complaint_review", "pending_payout", "paid_out"];

const TAB_FILTER: Record<Tab, (a: AuctionRow) => boolean> = {
  all: () => true,
  live: (a) => a.status === "live",
  pending_review: (a) => a.status === "pending_review",
  changes_requested: (a) => a.status === "changes_requested",
  draft: (a) => a.status === "draft",
  closed: (a) => CLOSED_STATUSES.includes(a.status),
};

export function AuksjonerAdminPage() {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<AuctionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<Tab>("all");

  useEffect(() => {
    fetch(`${API}/api/admin/auctions`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setAuctions(Array.isArray(d) ? d : []))
            .catch(() => { /* network error – silently handled */ })
      .finally(() => setLoading(false));
  }, []);

  const filtered = auctions.filter((a) => {
    const q = search.toLowerCase();
    const matchesSearch =
      a.title.toLowerCase().includes(q) ||
      a.orgName.toLowerCase().includes(q) ||
      [a.modelYear, a.make, a.model].filter(Boolean).join(" ").toLowerCase().includes(q);
    return matchesSearch && TAB_FILTER[tab](a);
  });

  const counts = {
    all: auctions.length,
    live: auctions.filter(TAB_FILTER.live).length,
    pending_review: auctions.filter(TAB_FILTER.pending_review).length,
    changes_requested: auctions.filter(TAB_FILTER.changes_requested).length,
    draft: auctions.filter(TAB_FILTER.draft).length,
    closed: auctions.filter(TAB_FILTER.closed).length,
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
            Auksjoner
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {loading ? "Laster..." : `${filtered.length} av ${auctions.length} auksjoner`}
          </p>
        </div>
        <Button onClick={() => navigate("/auksjoner/opprett")}>
          <Plus size={16} style={{ marginRight: 6 }} />
          Opprett auksjon
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Søk etter auksjon, bil eller organisasjon..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="flex-shrink-0">
          <TabsList>
            <TabsTrigger value="all">Alle ({counts.all})</TabsTrigger>
            <TabsTrigger value="live">Live ({counts.live})</TabsTrigger>
            <TabsTrigger value="pending_review">Til godkjenning ({counts.pending_review})</TabsTrigger>
            <TabsTrigger value="changes_requested">Endringer ønsket ({counts.changes_requested})</TabsTrigger>
            <TabsTrigger value="draft">Utkast ({counts.draft})</TabsTrigger>
            <TabsTrigger value="closed">Avsluttet ({counts.closed})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Table — desktop */}
      <Card className="hidden sm:block overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Auksjon</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organisasjon</th>
              <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bud</th>
              <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Høyeste bud</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Transport</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Slutter</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">Laster auksjoner...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">Ingen auksjoner funnet</td></tr>
            ) : (
              filtered.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/auksjoner/${a.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{a.title}</p>
                      {a.type === "flash" && (
                        <Badge variant="accent" className="h-4 px-1.5 text-[9px] font-black uppercase">Lyn</Badge>
                      )}
                    </div>
                    {(a.make || a.model) && (
                      <p className="text-xs text-muted-foreground">{[a.modelYear, a.make, a.model].filter(Boolean).join(" ")}</p>
                    )}
                    <p className="text-[10px] font-mono text-muted-foreground/60">#{a.referenceNumber}</p>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{a.orgName}</td>
                  <td className="px-6 py-4 text-right font-semibold">{a.bidCount}</td>
                  <td className="px-6 py-4 text-right">
                    {a.currentBid > 0 ? `${a.currentBid.toLocaleString("nb-NO")} kr` : "—"}
                  </td>
                  <td className="px-6 py-4"><AuctionStatusBadge status={a.status} /></td>
                  <td className="px-6 py-4">
                    <Badge variant={a.transportOrdered ? "success" : "muted"}>
                      {a.transportOrdered ? "Bestilt" : "Ikke bestilt"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{a.endsAt ? formatDate(a.endsAt) : "—"}</td>
                  <td className="px-6 py-4"><ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <p className="text-center text-sm text-muted-foreground py-8">Laster auksjoner...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">Ingen auksjoner funnet</p>
        ) : (
          filtered.map((a) => (
            <Card key={a.id} className="p-4 cursor-pointer" onClick={() => navigate(`/auksjoner/${a.id}`)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-foreground truncate">{a.title}</p>
                    {a.type === "flash" && (
                      <Badge variant="accent" className="h-4 px-1.5 text-[9px] font-black uppercase">Lyn</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{a.orgName}</p>
                  <p className="text-[10px] font-mono text-muted-foreground/60">#{a.referenceNumber}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-xs text-muted-foreground">{a.bidCount} bud</span>
                    {a.currentBid > 0 && (
                      <span className="text-xs font-semibold text-foreground">{a.currentBid.toLocaleString("nb-NO")} kr</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <AuctionStatusBadge status={a.status} />
                  <Badge variant={a.transportOrdered ? "success" : "muted"}>
                    {a.transportOrdered ? "Transport bestilt" : "Ingen transport"}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">{a.endsAt ? formatDate(a.endsAt) : "—"}</span>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

function AuctionStatusBadge({ status }: { status: string }) {
  if (status === "live") return <Badge variant="success">Live</Badge>;
  if (status === "paused") return <Badge variant="accent">Pauset</Badge>;
  if (status === "scheduled") return <Badge variant="accent">Planlagt</Badge>;
  if (status === "pending_review") return <Badge variant="warning">Til godkjenning</Badge>;
  if (status === "approved") return <Badge variant="accent">Godkjent</Badge>;
  if (status === "changes_requested") return <Badge variant="destructive">Endringer ønsket</Badge>;
  if (status === "pending_seller_decision") return <Badge variant="warning">Avventer beslutning</Badge>;
  if (status === "negotiating") return <Badge variant="warning">Forhandling</Badge>;
  if (status === "no_bids") return <Badge variant="secondary">Ingen bud</Badge>;
  if (status === "sold") return <Badge variant="success">Solgt</Badge>;
  if (status === "no_sale") return <Badge variant="secondary">Ikke solgt</Badge>;
  if (status === "contract_pending") return <Badge variant="accent">Kontrakt sendt</Badge>;
  if (status === "invoice_pending") return <Badge variant="warning">Faktura utestående</Badge>;
  if (status === "payment_pending") return <Badge variant="warning">Avventer betaling</Badge>;
  if (status === "ready_for_pickup") return <Badge variant="accent">Klar til å hente</Badge>;
  if (status === "picked_up") return <Badge variant="accent">Hentet av transportør</Badge>;
  if (status === "in_transit") return <Badge variant="accent">Under transport</Badge>;
  if (status === "delivered") return <Badge variant="success">Levert</Badge>;
  if (status === "complaint_review") return <Badge variant="destructive">Reklamasjon</Badge>;
  if (status === "pending_payout") return <Badge variant="warning">Avventer utbetaling</Badge>;
  if (status === "paid_out") return <Badge variant="success">Utbetalt</Badge>;
  return <Badge variant="muted">Utkast</Badge>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
